import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from "@mui/material";

const CveTable = ({
  filteredPlugins = [],
  handleRowClick,

}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Mémoïsé : identité stable tant que filteredPlugins ne change pas.
  const flattenedData = useMemo(
    () =>
      filteredPlugins.flatMap(group => (group.plugins || []).map(p => ({
        ...p,
        // `cves` = toutes les CVE du vendor, `impactedCves` = celles qui touchent les
        // versions installées. Les deux doivent rester distinctes.
        cves: p.cves || [],
        impactedCves: p.impactedCves || [],
        sitesArray: p.sitesArray || (p.sites || []).map(s => s.siteUrl).filter(Boolean),
      }))),
    [filteredPlugins]
  );

  const allServices = useMemo(() => {
    const serviceSet = new Set();
    flattenedData.forEach((plugin) => {
      (plugin.sitesArray || []).forEach((svc) => serviceSet.add(svc));
    });
    return ["All", ...Array.from(serviceSet).sort()];
  }, [flattenedData]);

  const [serviceFilter, setServiceFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Liste dérivée (filtre + tri) : plus de state ni d'effet, donc plus de boucle de render
  const displayedPlugins = useMemo(() => {
    let newList = [...flattenedData];

    if (serviceFilter !== "All") {
      newList = newList.filter((plugin) =>
        (plugin.sitesArray || []).some(
          (svc) => svc.toLowerCase() === serviceFilter.toLowerCase()
        )
      );
    }

    newList.sort((a, b) => {
      // Déterminer si le plugin est sur un site PRD
      const aHasPRD = (a.sites || []).some(site => site.env === "PRD");
      const bHasPRD = (b.sites || []).some(site => site.env === "PRD");

      // Déterminer si la sévérité est élevée (≥ 7)
      const aIsHighSeverity = a.severity !== null && a.severity >= 7;
      const bIsHighSeverity = b.severity !== null && b.severity >= 7;

      // Calculer le score de priorité (plus c'est élevé, plus c'est critique)
      const aScore = (aIsHighSeverity ? 10 : 0) + (aHasPRD ? 5 : 0) + (a.severity || 0);
      const bScore = (bIsHighSeverity ? 10 : 0) + (bHasPRD ? 5 : 0) + (b.severity || 0);
      
      return bScore - aScore;
    });

    return newList;
  }, [flattenedData, serviceFilter]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayedPlugins.slice(indexOfFirstItem, indexOfLastItem);

  const handleChangePage = (event, newPage) => setCurrentPage(newPage);
  const handleChangeItemsPerPage = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleServiceFilterChange = (e) => {
    setServiceFilter(e.target.value);
  };


  // Nombre max de sites affichés dans la colonne "Sites impactés"
  const MAX_SITES_DISPLAYED = 3;

  //Affichage de la sévérité dans le tableau 
  const formatSeverity = (score) => {
    if (score === null) return "CVSS non précisé";
    if (score >= 9) return `Critique (CVSS ${score})`;
    if (score >= 7) return `Élevée (CVSS ${score})`;
    if (score >= 4) return `Moyenne (CVSS ${score})`;
    return `Faible (CVSS ${score})`;
  };


  // Affichage des sites impactés : au-delà de MAX_SITES_DISPLAYED, on tronque
  // et on signale le reste par un "[...]" (les sites masqués sont en title)
  const renderImpactedSites = (sites) => {
    const hidden = sites.slice(MAX_SITES_DISPLAYED);
    return (
      <>
        {sites.slice(0, MAX_SITES_DISPLAYED).map((site, i) => (
          <a
            key={i}
            href={site}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ color: "#1976d2", textDecoration: "none", marginRight: "4px" }}
          >
            {site}
          </a>
        ))}
        {hidden.length > 0 && (
          <span
            title={hidden.join(", ")}
            style={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}
          >
            [...]
          </span>
        )}
      </>
    );
  };

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2,borderTopLeftRadius: 0, overflow: 'hidden', boxShadow:2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Module</TableCell>
            <TableCell>Version Impactée</TableCell>
            <TableCell>Sévérité max.</TableCell>
            <TableCell>
              <FormControl variant="outlined" size="small">
                <InputLabel id="service-filter-label">Site</InputLabel>
                <Select
                  labelId="service-filter-label"
                  value={serviceFilter}
                  label="Site"
                  onChange={handleServiceFilterChange}
                  style={{ minWidth: 120 }}
                >
                  {allServices.map((svc) => (
                    <MenuItem key={svc} value={svc}>
                      {svc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentItems.map((plugin, index) => (
            <TableRow
              key={`${plugin.name}-${index}`}
              sx={{
                backgroundColor: index % 2 === 0
                  ? theme.palette.background.alternate
                  : theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: isDark
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                },
                cursor: 'pointer',
              }}
              onClick={() => handleRowClick(plugin)}
              title={plugin.impactedCves.map((c) => c.cve_id).join(", ")}
            >
              <TableCell>{plugin.name}</TableCell>
              <TableCell>
                Jusqu'à {plugin.maxImpactedVersion}
              </TableCell>
              <TableCell>{formatSeverity(plugin.severity)}</TableCell>
              <TableCell>
                {renderImpactedSites(
                  serviceFilter === "All"
                    ? plugin.sitesArray || []
                    : [serviceFilter]
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack spacing={2} direction="row" justifyContent="center" alignItems="center" padding={2}>
        <FormControl variant="outlined" size="small">
          <InputLabel id="items-per-page-label" sx={{
            color: isDark ? theme.palette.text.primary : 'black',
            '&.Mui-focused': {
              color: isDark ? theme.palette.text.primary : 'black',
            },
          }}>CVE</InputLabel>
          <Select
            labelId="items-per-page-label"
            value={itemsPerPage}
            label="Plugins"
            onChange={handleChangeItemsPerPage}
            sx={{
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark
                  ? 'rgba(255, 255, 255, 0.23)'
                  : '#F7E900',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#F7E900',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: isDark
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(247, 233, 0, 0.08)',
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: isDark
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(247, 233, 0, 0.12)',
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: isDark
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'rgba(247, 233, 0, 0.16)',
                  },
                },
              },
            }}
          >
            <MenuItem  value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          count={Math.max(1, Math.ceil(displayedPlugins.length / itemsPerPage))}
          page={currentPage}
          onChange={handleChangePage}
          variant="outlined"
          sx={{
            "& .MuiPaginationItem-root": {
              backgroundColor: isDark
                ? theme.palette.background.paper
                : "white",
              color: isDark
                ? theme.palette.text.primary
                : "black",
              "&.Mui-selected": {
                backgroundColor: "rgba(247, 233, 0, 0.4)",
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                color: isDark ? "white" : "black",
              },
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(247, 233, 0, 0.2)"
                  : "yellow",
                color: isDark
                  ? "white"
                  : "rgba(0, 0, 0, 0.23)",
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
              },
            }
          }}
        />
      </Stack>
    </TableContainer>
  );
};

export default CveTable;